import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SsoError } from 'src/app/core/_enums/sso.error';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { AuthService } from '../../core/_services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-sso',
  templateUrl: './sso.component.html',
  styleUrls: ['./sso.component.scss']
})
export class SsoComponent implements OnInit {
  messageKey = '';
  portalUrl = '';
  private wasSignedIn = false;
  private currentAccount = '';

  constructor(
    private activeRoute: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Lu avant l'appel : sur échec, la session en cours n'est pas touchée, mais le compte nommé dans le
    // message doit être celui affiché dans la barre du haut.
    const signedIn = this.authService.userValue;
    this.wasSignedIn = !!signedIn?.key;
    this.currentAccount = [signedIn?.userFirstName, signedIn?.userName].filter(Boolean).join(' ') || signedIn?.owner || '';

    // Le jeton est retiré de l'URL par le script en tête de index.html, avant le chargement des
    // balises tierces ; la lecture du paramètre ne sert que si ce script n'a pas tourné.
    const stripped = (window as any).__ssoToken;
    delete (window as any).__ssoToken;
    const token = stripped || this.activeRoute.snapshot.queryParamMap.get('token');

    if (!token) {
      this.backToLogin('token');
      return;
    }

    this.authService
      .ssoLogin(token)
      .pipe(first())
      .subscribe(
        (data) => {
          if (data?.success) {
            this.router.navigate(['/'], { replaceUrl: true });
            return;
          }
          this.handleError(data?.error);
        },
        () => this.backToLogin('token')
      );
  }

  private handleError(error: SsoError): void {
    if (this.keepCurrentSession()) {
      return;
    }

    switch (error) {
      case SsoError.AccountIsPending:
        this.messageKey = 'sso_account_pending';
        break;
      case SsoError.TokenExpired:
        this.messageKey = 'sso_token_expired';
        this.portalUrl = environment.fntpPortalUrl;
        break;
      case SsoError.TokenAlreadyUsed:
        this.messageKey = 'sso_token_already_used';
        this.portalUrl = environment.fntpPortalUrl;
        break;
      case SsoError.NoAccount:
        this.backToLogin('unknown');
        break;
      case SsoError.AmbiguousAccount:
        this.backToLogin('ambiguous');
        break;
      case SsoError.NoUsableRight:
        this.backToLogin('no-access');
        break;
      case SsoError.AccountIsLocked:
        this.backToLogin('locked');
        break;
      default:
        this.backToLogin('token');
    }
  }

  private backToLogin(reason: string): void {
    if (this.keepCurrentSession()) {
      return;
    }

    this.router.navigate([SiteMapUrl.MenuLogin], { queryParams: { sso: reason }, replaceUrl: true });
  }

  // Une session est déjà ouverte dans ce navigateur : l'échec du lien FNTP ne doit pas la faire perdre.
  // Aucun des messages détaillés ne convient alors, ils renvoient tous vers le formulaire de connexion.
  private keepCurrentSession(): boolean {
    if (!this.wasSignedIn) {
      return false;
    }

    this.snackbarService.showMessage(this.translate.instant('sso_failed_still_signed_in', { username: this.currentAccount }));
    this.router.navigate(['/'], { replaceUrl: true });
    return true;
  }
}
